// src/utils/axiosConfig.js
// Axios interceptor for automatic token refresh and logout
import axios from 'axios';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

export const setupAxiosInterceptors = (logout, refreshToken) => {
    // Request interceptor - add withCredentials to all requests
    axios.interceptors.request.use(
        (config) => {
            config.withCredentials = true; // Always send cookies
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Response interceptor - handle 401 errors
    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // Don't intercept auth check or logout endpoints (prevents infinite loop)
            if (
                originalRequest.url?.includes('/auth/check') ||
                originalRequest.url?.includes('/auth/logout') ||
                originalRequest.url?.includes('/auth/refresh')
            ) {
                return Promise.reject(error);
            }

            // If error is 401 and we haven't tried refreshing yet
            if (error.response?.status === 401 && !originalRequest._retry) {
                const errorCode = error.response?.data?.code;
                const errorMessage = error.response?.data?.error;
                const shouldAttemptRefresh =
                    // Our middleware explicitly marks access-token expiry
                    errorCode === 'TOKEN_EXPIRED' ||
                    // When the access-token cookie is missing/expired, middleware returns this
                    errorMessage === 'No token provided' ||
                    // Be defensive: some paths might return other wording
                    errorMessage === 'Token expired';

                if (shouldAttemptRefresh) {
                    // Access token mangler eller er utløpt - prøv å refresh og retry en gang

                    // Mark retry up-front to avoid repeated refresh loops
                    originalRequest._retry = true;

                    if (isRefreshing) {
                        // Already refreshing - queue this request
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        })
                            .then(() => {
                                return axios(originalRequest);
                            })
                            .catch((err) => {
                                return Promise.reject(err);
                            });
                    }

                    isRefreshing = true;

                    try {
                        // Attempt to refresh token
                        const refreshSuccess = await refreshToken();

                        if (refreshSuccess) {
                            processQueue(null);
                            return axios(originalRequest); // Retry original request
                        } else {
                            processQueue(error);
                            return Promise.reject(error);
                        }
                    } catch (refreshError) {
                        processQueue(refreshError);
                        // If refresh fails unexpectedly (network/server), fall back to logout
                        // to avoid leaving the app in a broken auth state.
                        logout();
                        window.location.href = '/login';
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshing = false;
                    }
                } else {
                    // Invalid token (not just expired) - logout immediately
                    logout();
                    window.location.href = '/login';
                }
            }

            return Promise.reject(error);
        }
    );
};

export default axios;
